import type { SmartCreationTargetingStrategyBranch } from "@adpilot/shared";
import { IconPlus, IconTargetArrow } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { FilterSelect } from "@/components/patterns/filter-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  sourceLabelKey,
  type TargetRow,
  type TargetSourceFilter,
  type TargetStatusFilter,
  type TargetTypeFilter,
  targetLabel,
} from "./strategy-target-model";

interface TargetCandidatesPaneProps {
  branch: SmartCreationTargetingStrategyBranch;
  candidateRows: TargetRow[];
  selectedTargetId: string | null;
  selectedCount: number;
  blockingCount: number;
  mutationPending: boolean;
  search: string;
  laneFilter: "active" | "all";
  typeFilter: TargetTypeFilter;
  sourceFilter: TargetSourceFilter;
  statusFilter: TargetStatusFilter;
  isLaneActive: (target: TargetRow) => boolean;
  onSearchChange: (search: string) => void;
  onLaneFilterChange: (filter: "active" | "all") => void;
  onTypeFilterChange: (filter: TargetTypeFilter) => void;
  onSourceFilterChange: (filter: TargetSourceFilter) => void;
  onStatusFilterChange: (filter: TargetStatusFilter) => void;
  onEnterTarget: () => void;
  onInspectTarget: (id: string) => void;
  onTargetIncludedChange: (target: TargetRow, included: boolean) => void;
}

export function TargetCandidatesPane({
  branch,
  candidateRows,
  selectedTargetId,
  selectedCount,
  blockingCount,
  mutationPending,
  search,
  laneFilter,
  typeFilter,
  sourceFilter,
  statusFilter,
  isLaneActive,
  onSearchChange,
  onLaneFilterChange,
  onTypeFilterChange,
  onSourceFilterChange,
  onStatusFilterChange,
  onEnterTarget,
  onInspectTarget,
  onTargetIncludedChange,
}: TargetCandidatesPaneProps) {
  const { t } = useTranslation("smartCreation");
  const typeItems = [
    { value: "all", label: t("strategy.editor.allTypes") },
    { value: "keyword", label: t("strategy.editor.keyword") },
    { value: "product", label: t("strategy.editor.product") },
    ...(branch.targets.some((target) => target.intent.type === "category")
      ? [{ value: "category", label: t("strategy.editor.category") }]
      : []),
    ...(branch.targets.some((target) => target.intent.type === "category_brand")
      ? [{ value: "category_brand", label: t("strategy.editor.categoryBrand") }]
      : []),
    ...(branch.kind === "automatic" ? [{ value: "auto", label: t("strategy.editor.auto") }] : []),
  ];
  const sourceItems = [
    { value: "all", label: t("strategy.editor.allSources") },
    { value: "entered", label: t("strategy.editor.userEntered") },
    { value: "legacy", label: t("strategy.editor.legacyDefault") },
    {
      value: "current_shop_search_term",
      label: t("strategy.editor.sources.current_shop_search_term"),
    },
    {
      value: "current_shop_target_performance",
      label: t("strategy.editor.sources.current_shop_target_performance"),
    },
    { value: "product_catalog", label: t("strategy.editor.sources.product_catalog") },
    {
      value: "current_shop_shared_history",
      label: t("strategy.editor.sources.current_shop_shared_history"),
    },
    { value: "user_entered_seed", label: t("strategy.editor.sources.user_entered_seed") },
    { value: "system_required", label: t("strategy.editor.sources.system_required") },
    {
      value: "amazon_recommendation",
      label: t("strategy.editor.sources.amazon_recommendation"),
    },
  ];
  const statusItems = [
    { value: "all", label: t("strategy.editor.allStatuses") },
    { value: "selected", label: t("strategy.editor.selectedStatus") },
    { value: "excluded", label: t("strategy.editor.excludedStatus") },
    { value: "issue", label: t("strategy.editor.diagnostics") },
  ];

  return (
    <DataTableFrame
      aria-label={t("strategy.editor.candidatesTitle")}
      data-target-pane="candidates"
      className="flex min-h-[442px] flex-col"
      viewportClassName="max-h-[440px] flex-1 overflow-auto"
      toolbar={
        <div className="flex w-full flex-col gap-2 py-3">
          <span className="w-full text-sm font-semibold">
            {t("strategy.editor.candidatesTitle")}
          </span>
          <SearchInput
            showIcon
            className="w-[220px]"
            value={search}
            onChange={(event) => onSearchChange(event.currentTarget.value)}
            placeholder={t("strategy.editor.search")}
          />
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              ariaLabel={t("strategy.editor.filterLane")}
              items={[
                { value: "active", label: t("strategy.editor.activeLanes") },
                { value: "all", label: t("strategy.editor.allLanes") },
              ]}
              value={laneFilter}
              onValueChange={(value) => onLaneFilterChange(value as "active" | "all")}
            />
            <FilterSelect
              ariaLabel={t("strategy.editor.filterType")}
              items={typeItems}
              value={typeFilter}
              onValueChange={(value) => onTypeFilterChange(value as TargetTypeFilter)}
            />
            <FilterSelect
              ariaLabel={t("strategy.editor.filterSource")}
              items={sourceItems}
              value={sourceFilter}
              onValueChange={(value) => onSourceFilterChange(value as TargetSourceFilter)}
            />
            <FilterSelect
              ariaLabel={t("strategy.editor.filterStatus")}
              items={statusItems}
              value={statusFilter}
              onValueChange={(value) => onStatusFilterChange(value as TargetStatusFilter)}
            />
            {branch.kind !== "automatic" ? (
              <Button type="button" size="sm" variant="outline" onClick={onEnterTarget}>
                <IconPlus data-icon="inline-start" />
                {t("strategy.editor.enterTarget")}
              </Button>
            ) : null}
          </div>
        </div>
      }
      footer={
        <>
          <span>{t("strategy.editor.countCandidates", { count: candidateRows.length })}</span>
          {blockingCount > 0 ? (
            <span className="text-destructive-foreground" aria-live="polite">
              {t("strategy.editor.blocking", { count: blockingCount })}
            </span>
          ) : null}
        </>
      }
    >
      {candidateRows.length === 0 ? (
        <Empty className="min-h-[320px] py-10">
          <EmptyMedia variant="icon">
            <IconTargetArrow />
          </EmptyMedia>
          <EmptyHeader>
            <EmptyTitle className="text-base">{t("strategy.editor.emptyTitle")}</EmptyTitle>
            <EmptyDescription className="text-xs">
              {t("strategy.editor.emptyDescription")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <DataTable density="compact" stickyHeader>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <span className="sr-only">{t("strategy.editor.selectedStatus")}</span>
              </TableHead>
              <TableHead>{t("strategy.editor.targetColumn")}</TableHead>
              <TableHead>{t("strategy.editor.typeColumn")}</TableHead>
              <TableHead>{t("strategy.editor.sourceColumn")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {candidateRows.map((target) => {
              const label = targetLabel(target.intent);
              return (
                <TableRow
                  key={target.id}
                  aria-selected={selectedTargetId === target.id}
                  className={cn(selectedTargetId === target.id && "bg-subtle")}
                >
                  <TableCell>
                    <Checkbox
                      checked={target.included}
                      disabled={
                        mutationPending ||
                        (branch.kind === "automatic" && target.included && selectedCount <= 1)
                      }
                      aria-label={t("strategy.editor.selectTarget", { target: label })}
                      onCheckedChange={(checked) => onTargetIncludedChange(target, checked)}
                    />
                  </TableCell>
                  <TableCell className="max-w-[180px]">
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto max-w-full justify-start overflow-hidden px-0 text-left text-[11px]"
                      aria-label={t("strategy.editor.inspectTarget", { target: label })}
                      onClick={() => onInspectTarget(target.id)}
                    >
                      <span className="truncate">{label}</span>
                    </Button>
                  </TableCell>
                  <TableCell className="capitalize">
                    {target.intent.type === "keyword"
                      ? t(`review.match.${target.intent.matchType}`)
                      : target.intent.type === "category_brand"
                        ? t("strategy.editor.categoryBrand")
                        : t(`strategy.editor.${target.intent.type}`)}
                  </TableCell>
                  <TableCell>
                    {target.candidate
                      ? t(`strategy.editor.sources.${target.candidate.source}`)
                      : t(sourceLabelKey(target))}
                    {!isLaneActive(target) ? (
                      <Badge className="ml-1" variant="neutral">
                        {t("strategy.editor.inactiveStatus")}
                      </Badge>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </DataTable>
      )}
    </DataTableFrame>
  );
}
